/**
 * Clients (iter-78) — dashboard-level list of every client the user
 * has invited to preview one of their books. Admin actions: add,
 * remove, copy preview link, approve/revoke download, resend email.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import { Copy, Check, ExternalLink, Trash2, Mail, RefreshCw, MessageSquare,
          UserPlus, Send, Lock, Eye, X } from "lucide-react";

const ONBOARDING_KEY = "bf360.clients.onboarding.dismissed";

export default function Clients() {
  const { user, loading } = useAuth();
  const [clients, setClients] = useState([]);
  const [books, setBooks] = useState([]);
  const [addForm, setAddForm] = useState({ email: "", name: "", bookId: "", sendEmail: true });
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState("");
  const [openThread, setOpenThread] = useState(null); // { clientId, bookId, comments: [] }
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try { return localStorage.getItem(ONBOARDING_KEY) !== "1"; }
    catch { return true; }
  });

  const dismissOnboarding = () => {
    try { localStorage.setItem(ONBOARDING_KEY, "1"); } catch { /* ignore */ }
    setShowOnboarding(false);
  };
  const restoreOnboarding = () => {
    try { localStorage.removeItem(ONBOARDING_KEY); } catch { /* ignore */ }
    setShowOnboarding(true);
  };

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const [c, b] = await Promise.all([
        api.get("/clients/all"),
        api.get("/books"),
      ]);
      setClients(c.data?.clients || []);
      setBooks(b.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { if (user) reload(); }, [user, reload]);

  const bookMap = useMemo(() => {
    const m = {};
    books.forEach((b) => { m[b.id] = b.title || "Untitled"; });
    return m;
  }, [books]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const addClient = async (e) => {
    e.preventDefault();
    const email = addForm.email.trim().toLowerCase();
    const bookId = addForm.bookId;
    if (!email) return toast.error("Client email is required");
    if (!bookId) return toast.error("Pick a book to share");
    setSaving(true);
    try {
      const { data } = await api.post(`/books/${bookId}/clients`, {
        email,
        name: addForm.name.trim() || null,
        send_email: addForm.sendEmail,
      });
      toast.success(
        addForm.sendEmail && data.email_sent
          ? `Invite emailed to ${email}`
          : `Client added — preview link ready`,
      );
      setAddForm({ email: "", name: "", bookId: "", sendEmail: true });
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const copyLink = async (id, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 1400);
      toast.success("Preview link copied");
    } catch { toast.error("Copy failed — long-press to select the link"); }
  };

  const toggleApproval = async (c) => {
    const next = !c.download_approved;
    try {
      await api.patch(`/books/${c.book_id}/clients/${c.id}/approve-download`, {
        approved: next,
      });
      toast.success(next ? "Downloads approved" : "Downloads paused");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Remove ${c.email}? Their preview link will stop working.`)) return;
    try {
      await api.delete(`/books/${c.book_id}/clients/${c.id}`);
      toast.success("Client removed");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resendEmail = async (c) => {
    try {
      const { data } = await api.post(`/books/${c.book_id}/clients/${c.id}/resend-email`);
      toast.success(data.email_sent ? "Email re-sent" : "Email queued (delivery pending)");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openComments = async (c) => {
    try {
      const { data } = await api.get(`/books/${c.book_id}/clients/${c.id}/comments`);
      setOpenThread({ clientId: c.id, bookId: c.book_id, email: c.email,
                       title: c.book_title, comments: data?.comments || [] });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="clients-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">§ Formatting service</p>
            <h1 className="font-editorial text-3xl mt-1">Clients</h1>
            <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-xl">
              Add clients manually and share a preview link. They can read and
              comment without an account. Downloads are watermarked and unlock
              only after you approve them.
            </p>
          </div>
          <Link to="/dashboard" className="btn-ghost text-xs">← Dashboard</Link>
        </div>

        {!showOnboarding && (
          <button onClick={restoreOnboarding}
                  className="text-xs text-[color:var(--primary)] hover:underline mb-4"
                  data-testid="clients-show-onboarding">
            Show me how the client workflow works
          </button>
        )}

        {showOnboarding && (
          <section className="border border-[color:var(--border)] bg-[#fbf8f0] p-5 mb-6 relative"
                     data-testid="clients-onboarding">
            <button onClick={dismissOnboarding}
                    className="absolute top-3 right-3 text-[color:var(--text-muted)] hover:text-black"
                    aria-label="Dismiss"
                    data-testid="clients-onboarding-dismiss">
              <X className="w-4 h-4" strokeWidth={1.5}/>
            </button>
            <p className="uppercase text-[10px] tracking-[0.25em] text-[color:var(--primary)]">§ How it works</p>
            <h2 className="font-editorial text-2xl mt-1">Deliver your first paid formatting job</h2>
            <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-2xl">
              Perfect for authors, publishers, ghost-writers, and anyone selling
              book-formatting as a service. Your client sees a private, watermarked
              preview — they can't share, screenshot-strip, or download until you say so.
            </p>

            <ol className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-4">
              <li className="border-l-2 border-[color:var(--primary)] pl-3">
                <div className="flex items-center gap-2 text-[color:var(--primary)]">
                  <UserPlus className="w-4 h-4" strokeWidth={1.5}/>
                  <span className="text-[10px] uppercase tracking-widest">Step 1</span>
                </div>
                <p className="font-editorial text-lg mt-1">Add the client</p>
                <p className="text-xs text-[color:var(--text-muted)] mt-1">
                  Enter their email, pick which book they'll preview, and hit
                  <em> Add client</em>. Every client uses one seat on your plan.
                </p>
              </li>
              <li className="border-l-2 border-[color:var(--primary)] pl-3">
                <div className="flex items-center gap-2 text-[color:var(--primary)]">
                  <Send className="w-4 h-4" strokeWidth={1.5}/>
                  <span className="text-[10px] uppercase tracking-widest">Step 2</span>
                </div>
                <p className="font-editorial text-lg mt-1">Share the link</p>
                <p className="text-xs text-[color:var(--text-muted)] mt-1">
                  We email a private preview link from
                  <span className="font-mono"> auth@bookforge360.com</span> —
                  or copy the link and DM it yourself. No client login required.
                </p>
              </li>
              <li className="border-l-2 border-[color:var(--primary)] pl-3">
                <div className="flex items-center gap-2 text-[color:var(--primary)]">
                  <MessageSquare className="w-4 h-4" strokeWidth={1.5}/>
                  <span className="text-[10px] uppercase tracking-widest">Step 3</span>
                </div>
                <p className="font-editorial text-lg mt-1">Collect feedback</p>
                <p className="text-xs text-[color:var(--text-muted)] mt-1">
                  Client reads and leaves comments right on the preview page.
                  You see every note under <em>View comments</em> on the row below.
                </p>
              </li>
              <li className="border-l-2 border-[color:var(--primary)] pl-3">
                <div className="flex items-center gap-2 text-[color:var(--primary)]">
                  <Lock className="w-4 h-4" strokeWidth={1.5}/>
                  <span className="text-[10px] uppercase tracking-widest">Step 4</span>
                </div>
                <p className="font-editorial text-lg mt-1">Approve download</p>
                <p className="text-xs text-[color:var(--text-muted)] mt-1">
                  Once you're paid, flip the <em>Downloads approved</em> switch.
                  The client can now download a watermarked PDF — every page.
                </p>
              </li>
            </ol>

            <div className="mt-5 border-t border-[color:var(--border)] pt-4 flex items-start gap-3">
              <div className="hidden sm:block w-10 h-10 shrink-0 bg-[color:var(--primary)]/10 flex items-center justify-center">
                <Eye className="w-5 h-5 text-[color:var(--primary)]" strokeWidth={1.5}/>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">What your client sees</p>
                <p className="text-xs text-[color:var(--text-muted)] mt-1">
                  A read-only page with your book preview in an embedded PDF,
                  a comments panel on the right, and a <strong>Download watermarked PDF</strong>
                  button that stays greyed-out (<em>Downloads locked</em>) until you approve.
                  Every page is stamped with a red diagonal <span className="font-mono">PREVIEW · &lt;their email&gt;</span>
                  so leaked copies trace back to the recipient.
                </p>
              </div>
            </div>

            <button onClick={dismissOnboarding}
                    className="btn-ghost text-xs mt-4"
                    data-testid="clients-onboarding-got-it">
              Got it — hide this
            </button>
          </section>
        )}

        <form onSubmit={addClient}
                className="border border-[color:var(--border)] bg-white p-4 mb-8 space-y-3"
                data-testid="client-add-form">
          <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Invite a client
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label>
              <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">
                Client email *
              </span>
              <input value={addForm.email} required type="email"
                     onChange={(e)=>setAddForm({...addForm, email: e.target.value})}
                     placeholder="client@example.com"
                     className="w-full border border-[color:var(--border)] px-3 py-2 text-sm"
                     data-testid="client-add-email"/>
            </label>
            <label>
              <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">
                Name (optional)
              </span>
              <input value={addForm.name}
                     onChange={(e)=>setAddForm({...addForm, name: e.target.value})}
                     placeholder="Jane Doe"
                     className="w-full border border-[color:var(--border)] px-3 py-2 text-sm"
                     data-testid="client-add-name"/>
            </label>
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest mb-1 text-[color:var(--text-muted)]">
              Book to share *
            </span>
            <select value={addForm.bookId} required
                    onChange={(e)=>setAddForm({...addForm, bookId: e.target.value})}
                    className="w-full border border-[color:var(--border)] px-3 py-2 text-sm bg-white"
                    data-testid="client-add-book">
              <option value="">— Select a book —</option>
              {books.map((b) => (
                <option key={b.id} value={b.id}>{b.title || "Untitled"}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-[color:var(--text-muted)]">
            <input type="checkbox" checked={addForm.sendEmail}
                   onChange={(e)=>setAddForm({...addForm, sendEmail: e.target.checked})}
                   data-testid="client-add-sendemail"/>
            Send preview link to their email (via <span className="font-mono">auth@bookforge360.com</span>)
          </label>
          <button type="submit" disabled={saving} className="btn-primary text-sm"
                  data-testid="client-add-submit">
            {saving ? "Adding…" : "Add client & copy link"}
          </button>
        </form>

        {busy && clients.length === 0 && (
          <p className="text-sm text-[color:var(--text-muted)]">Loading…</p>
        )}
        {!busy && clients.length === 0 && (
          <p className="text-sm text-[color:var(--text-muted)] italic" data-testid="clients-empty">
            No clients yet. Add a client above to share a private preview link.
          </p>
        )}

        <div className="space-y-4">
          {clients.map((c) => (
            <section key={c.id}
                     className="border border-[color:var(--border)] bg-white p-4"
                     data-testid={`client-row-${c.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-semibold text-base truncate">{c.name || c.email}</p>
                  <p className="text-xs text-[color:var(--text-muted)] truncate">
                    <span className="font-mono">{c.email}</span> · viewing <em>{c.book_title || bookMap[c.book_id]}</em>
                  </p>
                  <p className="text-[11px] text-[color:var(--text-muted)] mt-1">
                    {c.views} view{c.views === 1 ? "" : "s"}
                    {c.last_viewed_at && <> · last opened {new Date(c.last_viewed_at).toLocaleString()}</>}
                    {c.comment_count > 0 && <> · {c.comment_count} comment{c.comment_count === 1 ? "" : "s"}</>}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <label className="flex items-center gap-2 text-xs cursor-pointer"
                          data-testid={`client-approve-${c.id}`}>
                    <input type="checkbox" checked={!!c.download_approved}
                           onChange={()=>toggleApproval(c)}/>
                    <span className={c.download_approved ? "text-green-700 font-medium" : "text-[color:var(--text-muted)]"}>
                      {c.download_approved ? "Downloads approved" : "Downloads locked"}
                    </span>
                  </label>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button onClick={()=>copyLink(c.id, c.preview_url)}
                        className="btn-ghost text-xs inline-flex items-center gap-1"
                        data-testid={`client-copy-${c.id}`}>
                  {copiedId === c.id
                    ? <><Check className="w-3 h-3" strokeWidth={1.5}/> Copied</>
                    : <><Copy className="w-3 h-3" strokeWidth={1.5}/> Copy preview link</>}
                </button>
                <a href={c.preview_url} target="_blank" rel="noreferrer"
                    className="btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid={`client-open-${c.id}`}>
                  <ExternalLink className="w-3 h-3" strokeWidth={1.5}/> Open preview
                </a>
                <button onClick={()=>resendEmail(c)}
                        className="btn-ghost text-xs inline-flex items-center gap-1"
                        data-testid={`client-resend-${c.id}`}>
                  <Mail className="w-3 h-3" strokeWidth={1.5}/> Re-send email
                </button>
                {c.comment_count > 0 && (
                  <button onClick={()=>openComments(c)}
                          className="btn-ghost text-xs inline-flex items-center gap-1"
                          data-testid={`client-comments-${c.id}`}>
                    <MessageSquare className="w-3 h-3" strokeWidth={1.5}/>
                    View {c.comment_count} comment{c.comment_count === 1 ? "" : "s"}
                  </button>
                )}
                <button onClick={()=>removeClient(c)}
                        className="text-xs text-red-700 hover:underline inline-flex items-center gap-1 ml-auto"
                        data-testid={`client-remove-${c.id}`}>
                  <Trash2 className="w-3 h-3" strokeWidth={1.5}/> Remove
                </button>
              </div>
            </section>
          ))}
        </div>
      </div>

      {openThread && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={()=>setOpenThread(null)}>
          <div className="bg-white max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col"
                onClick={(e)=>e.stopPropagation()}
                data-testid="client-comments-modal">
            <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                  Comments from {openThread.email}
                </p>
                <h3 className="font-editorial text-lg mt-1">{openThread.title}</h3>
              </div>
              <button onClick={()=>setOpenThread(null)}
                      className="text-sm text-[color:var(--text-muted)]"
                      data-testid="client-comments-close">Close</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              {openThread.comments.length === 0 && (
                <p className="text-sm text-[color:var(--text-muted)] italic">No comments yet.</p>
              )}
              {openThread.comments.map((c) => (
                <div key={c.id} className="border-l-2 border-[color:var(--primary)] pl-3 py-1">
                  <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                    {new Date(c.created_at).toLocaleString()}
                    {c.chapter_index != null && <> · Chapter {c.chapter_index + 1}</>}
                  </p>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{c.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
